import { useEffect, useState } from "react";
import { followupAPI } from "../../lib/api";
import { ListChecks, Plus, Trash2, Edit, X, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const SEGMENTOS = [
    { value: "mulheres_com_proposito", label: "Mulheres com Propósito" },
    { value: "mesa_de_ester",          label: "Mesa de Ester" },
    { value: "luto_ao_recomeco",       label: "Do Luto ao Recomeço" },
    { value: "devocional",             label: "Devocional" },
    { value: "musica",                 label: "Música" },
    { value: "livro",                  label: "Livro" },
    { value: "geral",                  label: "Geral" },
];

const EMPTY = { name: "", segmento: "geral", canal: "email", trigger_days: 3, message: "", active: true };

function RuleModal({ open, onClose, onSaved, editing }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(editing ? { ...editing } : EMPTY); }, [editing, open]);

    const save = async () => {
        if (!form.name) { toast.error("Nome obrigatório"); return; }
        setSaving(true);
        try {
            if (editing) await followupAPI.update(editing.id, form);
            else         await followupAPI.create(form);
            toast.success(editing ? "Regra atualizada" : "Regra criada");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
        finally { setSaving(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-md shadow-2xl">
                <div className="p-5 border-b border-stone-800 flex items-center justify-between">
                    <h2 className="font-bold text-stone-100">{editing ? "Editar" : "Nova"} Regra de Followup</h2>
                    <button onClick={onClose} className="text-stone-500 hover:text-stone-300"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Nome</label>
                        <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="ex: Followup D+3 Devocional" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Segmento</label>
                            <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.segmento} onChange={e => setForm(f => ({...f, segmento: e.target.value}))}>
                                {SEGMENTOS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Canal</label>
                            <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.canal} onChange={e => setForm(f => ({...f, canal: e.target.value}))}>
                                <option value="email">Email</option>
                                <option value="whatsapp">WhatsApp</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Disparar após (dias do cadastro)</label>
                        <input type="number" min={1} max={90} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.trigger_days} onChange={e => setForm(f => ({...f, trigger_days: Number(e.target.value)}))} />
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Mensagem</label>
                        <textarea rows={4} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600 resize-none"
                            value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} placeholder="Conteúdo da mensagem de followup..." />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
                        <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({...f, active: e.target.checked}))} />
                        Regra ativa
                    </label>
                </div>
                <div className="p-5 border-t border-stone-800 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-stone-400 hover:text-stone-200">Cancelar</button>
                    <button onClick={save} disabled={saving}
                        className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50 transition-colors">
                        {saving ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function FollowupRulesAdmin() {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try { const r = await followupAPI.list(); setRules(r.data || []); }
        catch { toast.error("Erro ao carregar regras"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Excluir regra?")) return;
        try { await followupAPI.delete(id); toast.success("Excluída"); load(); }
        catch { toast.error("Erro ao excluir"); }
    };

    return (
        <>
        <Header title="Followup Rules" subtitle="Automações por tempo após cadastro" actions={<button onClick={() => setModal(true)} className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Nova Regra</button>} />
        <div className="p-6 space-y-5">
            <RuleModal open={modal} onClose={() => { setModal(false); setEditing(null); }}
                onSaved={load} editing={editing} />

            {loading ? (
                <div className="text-stone-500 text-sm text-center py-16">Carregando...</div>
            ) : rules.length === 0 ? (
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-12 text-center">
                    <ListChecks className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                    <p className="text-stone-400 text-sm">Nenhuma regra configurada.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {rules.map(rule => (
                        <div key={rule.id} className="bg-stone-900 border border-stone-800 rounded-xl p-4 flex items-center gap-4 hover:border-stone-700 transition-all">
                            <div className={`p-2 rounded-lg ${rule.canal === "whatsapp" ? "bg-green-900/30" : "bg-sky-900/30"}`}>
                                {rule.canal === "whatsapp" ? <MessageCircle className="w-4 h-4 text-green-400" /> : <Mail className="w-4 h-4 text-sky-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-stone-100">{rule.name}</p>
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full border ${rule.active ? "bg-green-900/30 text-green-300 border-green-700" : "bg-stone-800 text-stone-500 border-stone-700"}`}>
                                        {rule.active ? "Ativa" : "Inativa"}
                                    </span>
                                </div>
                                <p className="text-xs text-stone-500 mt-0.5">
                                    {SEGMENTOS.find(s => s.value === rule.segmento)?.label} · Dispara em D+{rule.trigger_days}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => { setEditing(rule); setModal(true); }}
                                    className="p-1.5 text-stone-400 hover:text-stone-100 hover:bg-stone-800 rounded-lg transition-colors">
                                    <Edit className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => del(rule.id)}
                                    className="p-1.5 text-stone-500 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
        </>
    );
}
